import { useCallback } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { Text, Row, Heading } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import { Avatar } from '@/pages/Article/components/styled';
import {
  RecommendedArticleWrapper,
  RecommendedArticleItem,
  StyledArticleOutline,
  StyledMarketInsightsOutline,
  StyledLink,
} from './styled';
import type { Article } from '../../hooks/useArticle';

type ArticleType = 'MarketInsights' | 'StockAnalysis';

interface LinkComponentProps {
  slug: string;
  canonicalUrl: string;
  children: ReactNode;
  onClick: () => void;
}

interface Props {
  articles:
    | Article['recommendedHumanArticles']
    | Article['recommendedMarketArticles'];
  articleType: ArticleType;
  linkComponent: (
    props: LinkComponentProps
  ) => ReactElement<LinkComponentProps>;
}

export const RecommendedArticles = ({
  articles,
  articleType,
  linkComponent: LinkComponent,
}: Props) => {
  const { trackEvent } = useTracking();
  const trackArticleClicked = useCallback(
    (slug) =>
      trackEvent({
        type: 'track',
        subject:
          articleType === 'MarketInsights'
            ? 'latest_market_insight_article'
            : 'latest_human_article',
        action: 'click',
        slug,
      }),
    [articleType, trackEvent]
  );

  return (
    <RecommendedArticleWrapper>
      <Heading typography="xLargeStrong" color="darkGray" as="h3">
        {articleType === 'MarketInsights' ? (
          <>
            <StyledMarketInsightsOutline width="40" height="40" />
            Market Insights
          </>
        ) : (
          <>
            <StyledArticleOutline fill="#fff" width="40" height="40" />
            Stock Analysis
          </>
        )}
      </Heading>
      {articles.length > 0 ? (
        articles.map((article, index) => (
          <RecommendedArticleItem key={index}>
            <LinkComponent
              slug={article.slug}
              canonicalUrl={article.canonicalURL}
              onClick={() => trackArticleClicked(article.canonicalURL)}
            >
              {article.title}
            </LinkComponent>
            <Row alignItem="center">
              <Avatar
                src={article.author?.photoURL || '/news/images/blue-bear.svg'}
                alt={article.author?.name || 'Simply Wall St'}
              />
              <StyledLink
                href={`https://simplywall.st/news/about-simply-wall-st-news/${
                  article.author?.slug || ''
                }`}
                target="_blank"
                onClick={() => {
                  trackEvent({
                    type: 'track',
                    subject: 'recommended_article_author',
                    action: 'click',
                    slug: article.author?.slug,
                  });
                }}
              >
                {article.author?.name || 'Simply Wall St'}
              </StyledLink>
            </Row>
          </RecommendedArticleItem>
        ))
      ) : (
        <Text color="black" typography="small">
          No articles found
        </Text>
      )}
    </RecommendedArticleWrapper>
  );
};
